import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, ArrowRight, Check } from 'lucide-react';

/* ════════════════════════════════════════════════════════════
   SiteFooter — Sitewide footer for The Alchemy Village
   • Social: Facebook + Instagram (more channels easy to add)
   • Email capture → POST /api/email-capture (source: "Site Footer")
   • Quick links: Book, Gift, About, Welcome
   • Hidden on dedicated landing routes via Layout.jsx
   ════════════════════════════════════════════════════════════ */

const SOCIALS = [
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/CabriniGangstaChef',
    Icon: Facebook,
    label: 'Follow Cabrini on Facebook',
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/gangstachefcabrini/',
    Icon: Instagram,
    label: 'Follow Cabrini on Instagram',
  },
];

const QUICK_LINKS = [
  { label: 'Welcome', to: '/welcome' },
  { label: 'Life-Map', to: '/life-map' },
  { label: 'The Book', to: '/book' },
  { label: 'Free Chapter', to: '/gift' },
  { label: 'About Cabrini', to: '/about' },
  { label: 'Book Cabrini', to: '/book-cabrini' },
];

const API = process.env.REACT_APP_BACKEND_URL;
const YEAR = new Date().getFullYear();

export default function SiteFooter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/email-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source: 'Site Footer' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Something went wrong.');
      }
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Please try again.');
    }
  }

  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="sf-inner">
        {/* Brand column */}
        <div className="sf-col sf-col--brand">
          <h3 className="sf-brand">The Alchemy Village</h3>
          <p className="sf-tag">
            A digital sanctuary for women crossing thresholds.
            <br />
            Built by Cabrini with love, fire, and a little bit of magic.
          </p>

          <div className="sf-socials" data-testid="sf-socials">
            {SOCIALS.map(({ name, href, Icon, label }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                title={label}
                className="sf-social"
                data-testid={`sf-social-${name.toLowerCase()}`}
              >
                <Icon size={18} strokeWidth={1.8} />
              </a>
            ))}
          </div>
        </div>

        {/* Quick links column */}
        <div className="sf-col sf-col--links">
          <p className="sf-col-title">Wander The Village</p>
          <ul className="sf-links">
            {QUICK_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`sf-link-${l.to.replace('/', '')}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Email capture column */}
        <div className="sf-col sf-col--cta">
          <p className="sf-col-title">Join the Village</p>
          <p className="sf-cta-sub">
            Get whispers from Cabrini — book drops, soulful prompts, &
            invitations to new circles.
          </p>

          {status === 'success' ? (
            <div className="sf-success" data-testid="sf-email-success">
              <Check size={16} strokeWidth={2.4} />
              <span>You're in. Check your inbox, sister. 🌙</span>
            </div>
          ) : (
            <form
              className="sf-form"
              onSubmit={handleSubmit}
              data-testid="sf-email-form"
              noValidate
            >
              <div className="sf-field">
                <Mail size={15} className="sf-field-icon" aria-hidden="true" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  aria-label="Email address"
                  data-testid="sf-email-input"
                  disabled={status === 'loading'}
                />
                <button
                  type="submit"
                  disabled={status === 'loading' || !email.trim()}
                  data-testid="sf-email-submit"
                  aria-label="Join the mailing list"
                >
                  {status === 'loading' ? (
                    <span className="sf-dots" aria-hidden="true">
                      <span></span><span></span><span></span>
                    </span>
                  ) : (
                    <ArrowRight size={16} strokeWidth={2.2} />
                  )}
                </button>
              </div>
              {status === 'error' && (
                <p className="sf-error" data-testid="sf-email-error">
                  {errorMsg}
                </p>
              )}
              <p className="sf-fineprint">
                No spam. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="sf-bottom">
        <p className="sf-copy">
          © {YEAR} The Alchemy Village · All rights reserved
        </p>
        <p className="sf-credit">
          Crafted by <span className="sf-credit-name">Cabrini</span> &amp; The Village
        </p>
      </div>
    </footer>
  );
}
